import type { HTMLAttributes, ReactNode } from "react";
import {
  classNames,
  getFeedbackSemantics,
  type FeedbackTone,
} from "./component-model";

export interface StatusPillProps
  extends Omit<HTMLAttributes<HTMLSpanElement>, "children"> {
  readonly tone?: FeedbackTone;
  readonly icon?: ReactNode;
  readonly children: ReactNode;
}

export function StatusPill({
  tone = "neutral",
  icon,
  children,
  className,
  ...props
}: StatusPillProps) {
  return (
    <span
      {...props}
      className={classNames(
        "wh-status-pill",
        `wh-status-pill--${tone}`,
        className,
      )}
    >
      {icon && (
        <span className="wh-status-pill__icon" aria-hidden="true">
          {icon}
        </span>
      )}
      <span>{children}</span>
    </span>
  );
}

export interface StatusMessageProps {
  readonly tone?: FeedbackTone;
  readonly title: ReactNode;
  readonly children?: ReactNode;
  readonly icon?: ReactNode;
  readonly live?: boolean;
  readonly className?: string;
}

export function StatusMessage({
  tone = "neutral",
  title,
  children,
  icon,
  live = true,
  className,
}: StatusMessageProps) {
  const semantics = getFeedbackSemantics(tone, live);

  return (
    <section
      className={classNames(
        "wh-status-message",
        `wh-status-message--${tone}`,
        className,
      )}
      role={semantics.role}
      aria-live={semantics.liveMode}
      aria-atomic="true"
    >
      {icon && (
        <span className="wh-status-message__icon" aria-hidden="true">
          {icon}
        </span>
      )}
      <div>
        <strong>{title}</strong>
        {children && <div className="wh-status-message__body">{children}</div>}
      </div>
    </section>
  );
}
